import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

interface StandardSummary {
  standardCode: string;
  versionYear: number;
  publicationStatus: "Draft" | "DIS" | "FDIS" | "Published" | "Withdrawn";
  clauseCount: number;
  moduleCode: string;
  isActiveForOrg: boolean;
}

const standardLabel = (code: string, year: number): string =>
  `${code.replace(/^ISO/, "ISO ")}:${year}`;

export function Standards() {
  const { slug } = useParams<{ slug: string }>();
  const [standards, setStandards] = useState<StandardSummary[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api<{ standards: StandardSummary[] }>("/standards")
      .then((res) => {
        if (!cancelled) setStandards(res.standards);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Standards</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          The ISO standards available in ISO Clarity. Locked standards are not yet active for your
          organisation.
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {standards === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {standards.map((s) => {
            const label = standardLabel(s.standardCode, s.versionYear);
            const card = (
              <div
                className={[
                  "h-full rounded-lg border p-4 shadow-sm transition",
                  s.isActiveForOrg
                    ? "border-slate-200 bg-white hover:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-brand-500"
                    : "border-slate-200 bg-slate-50 opacity-70 dark:border-slate-700 dark:bg-slate-900",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {label}
                  </h2>
                  {s.isActiveForOrg ? (
                    <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-700 dark:bg-brand-500/10 dark:text-brand-100">
                      Active
                    </span>
                  ) : (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Locked
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  {s.clauseCount} clause{s.clauseCount === 1 ? "" : "s"} available
                </p>
                {!s.isActiveForOrg ? (
                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-500">
                    Upgrade your subscription to unlock this standard.
                  </p>
                ) : null}
              </div>
            );
            return s.isActiveForOrg ? (
              <Link
                key={s.moduleCode}
                to={`/o/${slug}/standards/${s.standardCode}/${s.versionYear}`}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-lg"
              >
                {card}
              </Link>
            ) : (
              <div key={s.moduleCode}>{card}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
